import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileText, Sparkles, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/app");
  }, [user, loading, navigate]);

  const handleLogin = () => navigate("/inloggen");

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#0F1E3D] text-white overflow-hidden">
        <div className="absolute bottom-0 inset-x-0 h-1.5 bg-[#C9A227]" />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #C9A227 0 1px, transparent 1px 22px)" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-white flex items-center justify-center p-1.5 shadow-sm ring-2 ring-[#C9A227]/70">
            <img src="/logo.png" alt="Praktisch-Debiteurenbeheer" className="h-full w-full object-contain" />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">Praktisch-Debiteurenbeheer</span>
        </div>
        <div className="relative z-10 space-y-6">
          <p className="text-[11px] tracking-[0.35em] font-semibold text-[#C9A227]">BEHEEROMGEVING</p>
          <h1 className="font-heading text-4xl font-extrabold leading-tight">Grip op debiteuren, betalingen en contracten.</h1>
          <p className="text-slate-300 text-lg max-w-md">Praktisch-Debiteurenbeheer verzorgt het betalings- en debiteurenbeheer namens opdrachtgevers — van contract tot aanmaning, overzichtelijk en stap voor stap.</p>
          <div className="space-y-3 pt-2">
            {[
              { icon: FileText, t: "Inzicht in betalingen en contracten op één plek" },
              { icon: Sparkles, t: "AI-gegenereerde herinneringen & aanmaningen" },
              { icon: ShieldCheck, t: "Volledige communicatie-tijdslijn als juridisch rapport" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-slate-200">
                <f.icon className="h-5 w-5 text-[#C9A227]" />
                <span>{f.t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-sm text-[#C9A227]/80">Slim debiteuren- en betalingsbeheer</div>
      </div>

      {/* Right login */}
      <div className="flex items-center justify-center p-8 bg-[#F7F5EF]">
        <div className="w-full max-w-md text-center space-y-8 animate-fade-in-up">
          <div className="lg:hidden flex items-center justify-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-white border border-[#C9A227]/60 flex items-center justify-center p-1.5">
              <img src="/logo.png" alt="Praktisch-Debiteurenbeheer" className="h-full w-full object-contain" />
            </div>
            <span className="font-heading text-xl font-bold text-[#0F1E3D]">Praktisch-Debiteurenbeheer</span>
          </div>
          <div className="space-y-2">
            <h2 className="font-heading text-3xl font-bold text-[#0F1E3D]">Welkom terug</h2>
            <div className="mx-auto h-0.5 w-12 bg-[#C9A227]" />
            <p className="text-slate-500">Log in op het management portaal</p>
          </div>
          <Button
            data-testid="email-login-button"
            onClick={handleLogin}
            className="w-full h-14 text-base gap-3 bg-[#0F1E3D] hover:bg-[#1B2D52] text-white border border-[#C9A227] shadow-sm hover:shadow-md transition-colors"
          >
            <ShieldCheck className="h-5 w-5 text-[#C9A227]" />
            Inloggen met e-mail
            <ArrowRight className="h-4 w-4 ml-auto text-[#C9A227]" />
          </Button>
          <p className="text-xs text-slate-400">Door in te loggen gaat u akkoord met veilige verwerking van uw gegevens.</p>
          <div className="pt-2 border-t border-[#C9A227]/30 space-y-2">
            <button onClick={() => (window.location.href = "/portaal")} data-testid="portal-login-link" className="text-sm text-[#0F1E3D] font-medium hover:text-[#C9A227] hover:underline block w-full">
              Bent u debiteur? Ga naar het debiteurenportaal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
